import React from "react";

/**
 * Overlay bloccante mostrato in dev quando `devGuard.js` rileva una
 * configurazione incoerente che causerebbe 401 sistematici (SameSite=Lax
 * cross-origin drop).
 *
 * Full-screen, non ignorabile, con istruzioni copy-paste. Sostituisce il
 * rendering di <App /> finche' la configurazione non viene corretta.
 *
 * No-op in produzione (il modulo devGuard non popola l'issue).
 */
export function DevGuardBanner({ issue }) {
  const box = {
    position: "fixed",
    inset: 0,
    background: "#0f172a",
    color: "#f8fafc",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    padding: "40px",
    overflow: "auto",
    zIndex: 999999,
  };
  const card = {
    maxWidth: 780,
    margin: "40px auto",
    background: "#1e293b",
    border: "2px solid #dc2626",
    borderRadius: 8,
    padding: "28px 32px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
  };
  const h1 = {
    color: "#fecaca",
    fontSize: 18,
    fontWeight: 700,
    margin: "0 0 8px",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };
  const h2 = { color: "#f8fafc", fontSize: 20, fontWeight: 600, margin: "0 0 20px" };
  const p = { color: "#cbd5e1", fontSize: 13, lineHeight: 1.6, margin: "12px 0" };
  const code = {
    display: "block",
    background: "#020617",
    color: "#7dd3fc",
    padding: "14px 16px",
    borderRadius: 4,
    fontSize: 12,
    lineHeight: 1.6,
    whiteSpace: "pre",
    overflow: "auto",
    margin: "16px 0",
    border: "1px solid #334155",
  };
  const kbd = {
    background: "#334155",
    color: "#f8fafc",
    padding: "1px 6px",
    borderRadius: 3,
    fontSize: 11,
  };

  return (
    <div style={box} data-testid="dev-guard-banner">
      <div style={card}>
        <div style={h1}>⚠ Configurazione dev incoerente</div>
        <h2 style={h2}>Il gestionale non partirà fino alla correzione di <code>frontend/.env</code></h2>

        <p style={p}>
          Rilevato: il frontend gira su <b>{issue.frontendOrigin}</b> ma le API sono
          configurate su <b>{issue.backendOrigin}</b> senza il proxy CRA attivo.
        </p>

        <p style={p}>
          <b style={{ color: "#fca5a5" }}>Conseguenza tecnica:</b> il backend
          emette il cookie di sessione con <code style={{ ...kbd, background: "#7f1d1d" }}>SameSite=Lax</code> e
          il browser lo scarterà silenziosamente perché cross-origin. Login andrà
          a buon fine (200 OK), ma tutte le chiamate successive risponderanno
          <code style={{ ...kbd, background: "#7f1d1d" }}>401 Non autenticato</code> senza
          Cookie header. Aziende, dipendenti e dashboard resteranno vuoti.
        </p>

        <p style={p}>
          <b style={{ color: "#86efac" }}>Fix definitivo — 30 secondi:</b>
        </p>

        <p style={p}>
          1) Apri il file <code style={kbd}>frontend/.env</code> con un editor di testo.
        </p>

        <p style={p}>
          2) Sostituisci l'intero contenuto con:
        </p>

        <code style={code}>{`PORT=3002
HOST=0.0.0.0
WDS_SOCKET_PORT=3002

# Proxy CRA: il dev-server inoltra /api/* al backend
# mantenendo il browser su origin unica → cookie SameSite=Lax OK.
PROXY_TARGET=${issue.backendOrigin}
REACT_APP_HAS_DEV_PROXY=1

# ATTENZIONE: NON reintrodurre REACT_APP_BACKEND_URL,
# altrimenti axios costruirà URL assoluti cross-origin e i
# cookie tornerebbero a essere scartati.`}</code>

        <p style={p}>
          3) Ferma il dev-server (<code style={kbd}>Ctrl+C</code>) e rilancia
          <code style={kbd}>npm start</code> (o <code style={kbd}>yarn start</code>).
        </p>

        <p style={p}>
          4) All'avvio dovresti vedere nel terminale il banner <b style={{ color: "#4ade80" }}>DEV PROXY ATTIVO</b> in verde. Questa pagina scomparirà automaticamente.
        </p>

        <p style={{ ...p, borderTop: "1px solid #334155", paddingTop: 16, marginTop: 24, fontSize: 12 }}>
          Documentazione: <code style={kbd}>frontend/.env.example</code> (SCELTA A/B) ·
          <code style={kbd}>README_LOCALE.md</code> § "Setup sviluppo con accesso da rete LAN"
        </p>
      </div>
    </div>
  );
}

export default DevGuardBanner;
